/**
 * Uma informação da ficha (E139.F3, ADR-0095 §2.7): o valor usado no exame e a situação, com o
 * texto impresso, o motivo, a divergência na linha e o cadastro ao lado; e a fonte, que abre a
 * página do arquivo recebido. E155.F3: "Rever leitura de um campo" abre o cartão na linha.
 *
 * E162.F2.T03 (GUS-142, ADR-0111 §2.6): à vista só o valor e o selo da situação (lido, em branco,
 * não localizado, ilegível…), com o aviso da divergência; a fonte, a página, o porquê, o valor
 * normalizado e o cadastro ficam em "Ver fonte e detalhe". Nada saiu da linha: só mudou de dobra.
 */
import { Badge, Button, TableCell, TableRow, type BadgeProps } from '@/components/ui';
import {
  paginaDaFonte,
  pedeAtencao,
  type CampoDaFichaData,
  type FonteDoCampoData,
  type PaginaNaFonte,
  type SituacaoDoCampo,
} from '@/lib/ficha_da_leitura';
import { podeReverLeitura } from '@/lib/releitura_da_leitura';

import { preencher, r, rotuloDoCampo, t, textoDaFonte } from './textos';

const TOM_DA_SITUACAO: Readonly<Record<SituacaoDoCampo, BadgeProps['variant']>> = {
  lido: 'success',
  em_branco: 'neutral',
  nao_localizado: 'warning',
  ilegivel: 'danger',
  nao_processado: 'neutral',
  deduzido: 'info',
};

function Divergencia({ fonte }: { fonte: FonteDoCampoData }): JSX.Element {
  return (
    <li data-testid="ficha-divergencia">
      {preencher(t.divergencia, {
        valor: fonte.literal ?? t.emBranco,
        fonte: textoDaFonte(fonte),
      })}
    </li>
  );
}

function ValorESelo({ campo }: { campo: CampoDaFichaData }): JSX.Element {
  const lido = campo.situacao === 'lido';
  return (
    <div className="space-y-1">
      {lido ? (
        <p className="font-semibold text-fg" data-testid="ficha-valor">
          {campo.literal ?? campo.valor ?? t.semValor}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-sc-1">
        <Badge variant={TOM_DA_SITUACAO[campo.situacao]} data-testid="ficha-selo">
          {t.situacao[campo.situacao]}
        </Badge>
        {campo.divergencias.length > 0 ? (
          <Badge variant="warning" data-testid="ficha-selo-divergencia">
            {t.seloDivergencia}
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

function Fonte({
  campo,
  onVerNaFonte,
}: {
  campo: CampoDaFichaData;
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
}): JSX.Element {
  const fonte = campo.fontes[0] ?? campo.divergencias[0];
  if (fonte === undefined) return <p className="text-fg-muted">{t.semFonte}</p>;
  const pagina = paginaDaFonte(fonte);
  return (
    <div className="space-y-0.5">
      <p className="text-fg" data-testid="ficha-fonte">
        {textoDaFonte(fonte)}
        {fonte.origem_do_texto === 'imagem' ? ` · ${t.origemImagem}` : ''}
      </p>
      {fonte.rotulo !== null ? <p className="text-body-sm text-fg-muted">{fonte.rotulo}</p> : null}
      {pagina !== null ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={() => onVerNaFonte(pagina)}
          data-testid="ficha-ver-na-fonte"
        >
          {t.verNaFonte}
        </Button>
      ) : null}
    </div>
  );
}

function Detalhe({
  campo,
  onVerNaFonte,
}: {
  campo: CampoDaFichaData;
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
}): JSX.Element {
  const lido = campo.situacao === 'lido';
  return (
    <details className="text-body-sm" data-testid="ficha-detalhe">
      <summary className="cursor-pointer text-accent-700">{t.verDetalhe}</summary>
      <div className="mt-sc-2 space-y-1">
        <Fonte campo={campo} onVerNaFonte={onVerNaFonte} />
        {campo.motivo !== 'rotulo_e_valor_no_papel' ? (
          <p className="text-fg-muted" data-testid="ficha-motivo">
            {t.motivo[campo.motivo]}
          </p>
        ) : null}
        {!lido && campo.valor !== null ? <p className="text-fg-muted">{campo.valor}</p> : null}
        {campo.divergencias.length > 0 ? (
          <ul className="text-warning-700">
            {campo.divergencias.map((fonte) => (
              <Divergencia
                key={`${fonte.documento_id}-${fonte.pagina}-${fonte.trecho}`}
                fonte={fonte}
              />
            ))}
          </ul>
        ) : null}
        {campo.no_cadastro !== null ? (
          <p className="text-fg-muted" data-testid="ficha-cadastro">
            {preencher(t.noCadastro, { valor: campo.no_cadastro })}
          </p>
        ) : null}
      </div>
    </details>
  );
}

export interface LinhaDoCampoProps {
  campo: CampoDaFichaData;
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
  /** O cartão "Rever leitura na fonte" está aberto nesta linha. */
  aberto?: boolean;
  /** Oferece "Rever leitura de um campo" (E155.F3); o instante da assinatura fica fora. */
  onReverLeitura?: ((campo: CampoDaFichaData) => void) | undefined;
}

export function LinhaDoCampo({
  campo,
  onVerNaFonte,
  aberto = false,
  onReverLeitura,
}: LinhaDoCampoProps): JSX.Element {
  const rever = onReverLeitura !== undefined && podeReverLeitura(campo.campo);
  return (
    <TableRow
      data-testid="ficha-campo"
      data-campo={campo.campo}
      data-situacao={campo.situacao}
      data-atencao={pedeAtencao(campo) ? 'sim' : 'nao'}
    >
      <TableCell className="align-top text-fg">
        <p>{rotuloDoCampo(campo)}</p>
        {rever ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="mt-1"
            aria-expanded={aberto}
            onClick={() => onReverLeitura(campo)}
            data-testid="ficha-rever"
          >
            {r.abrir}
          </Button>
        ) : null}
      </TableCell>
      <TableCell className="align-top">
        <ValorESelo campo={campo} />
      </TableCell>
      <TableCell className="align-top">
        <Detalhe campo={campo} onVerNaFonte={onVerNaFonte} />
      </TableCell>
    </TableRow>
  );
}
